import { useCallback, useMemo, useState } from 'react';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { hotjar } from '@hotjar/tracking';
import { useCurrentSite } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { downloadWithLink } from '@hotjar/webapp/lib/downloadWithLink';
import type { Clause } from '../../../domain/Surveys/Responses/Filters/Clause';
import { useDownloadSurveyResponsesMutation } from '../../../domain/Surveys/Export/download/useDownloadSurveyResponsesMutation';

export interface SurveyDownloadParams {
  surveyId: number;
  format: 'csv' | 'xlsx';
  sortBy?: string;
  clauses?: Clause[];
}

const useDownloadSurveyResponses = () => {
  const { data: currentSite } = useCurrentSite();
  const track = useTrack();

  const { showToast } = useNotify();
  const { t } = useTranslation('feedback');
  const downloadSurveyResponsesMutation = useDownloadSurveyResponsesMutation();

  return useCallback(
    async ({ surveyId, format, sortBy, clauses }: SurveyDownloadParams) => {
      try {
        if (!currentSite) throw new Error('No site or download format');
        showToast({ kind: 'info', text: t('survey-responses.export.started-message') });
        const result = await downloadSurveyResponsesMutation.mutateAsync({
          siteId: currentSite.id,
          surveyId,
          format,
          clauses,
          sortBy,
          isAsyncExport: false,
        });
        if (result.success === false) throw new Error('Error fetching the download URL');
        if ('above_threshold' in result) return 'tooLarge';
        if ('download_url' in result) {
          downloadWithLink(`survey-${surveyId}.${format}.zip`, result.download_url);
          track.download('Poll Responses', format, track.getGroupDataForSite(currentSite));
        }
      } catch (e) {
        showToast({ kind: 'danger', text: t('survey-list.notification.download-error') });
        throw e;
      }
      return 'success';
    },
    [currentSite, downloadSurveyResponsesMutation, showToast, track, t],
  );
};

const useDownloadSurveyResponsesAsync = () => {
  const { data: currentSite } = useCurrentSite();

  const { showToast } = useNotify();
  const { t } = useTranslation('feedback');
  const downloadSurveyResponsesMutation = useDownloadSurveyResponsesMutation();

  return useCallback(
    async ({ surveyId, format, sortBy, clauses }: SurveyDownloadParams) => {
      try {
        if (currentSite === undefined) throw new Error('No site or download format');
        showToast({ kind: 'info', text: t('survey-responses.export.started-message') });
        await downloadSurveyResponsesMutation.mutateAsync({
          siteId: currentSite.id,
          surveyId,
          format,
          sortBy,
          clauses,
          isAsyncExport: true,
        });
      } catch (e) {
        showToast({ kind: 'danger', text: t('survey-list.notification.download-error') });
        throw e;
      }
      return 'success';
    },
    [currentSite, downloadSurveyResponsesMutation, showToast, t],
  );
};

export const useDownloadActions = (): {
  isFileTooLarge: boolean;
  continueDownload: () => Promise<void>;
  handleDownload: (surveyDownloadParams: SurveyDownloadParams) => Promise<void>;
  cancelDownload: () => void;
} => {
  const [isFileTooLarge, setIsFileTooLarge] = useState(false);
  const [activeDownloadParams, setActiveDownloadParams] = useState<
    SurveyDownloadParams | undefined
  >();

  const onDownloadSurveyResponses = useDownloadSurveyResponses();
  const onDownloadSurveyResponsesAsync = useDownloadSurveyResponsesAsync();

  const cancelDownload = useCallback(() => {
    setActiveDownloadParams(undefined);
    setIsFileTooLarge(false);
  }, []);

  const continueDownload = useCallback(async () => {
    try {
      if (activeDownloadParams === undefined) throw new Error('Invalid download format');
      hotjar.event('downloaded_responses');
      await onDownloadSurveyResponsesAsync(activeDownloadParams);
    } finally {
      cancelDownload();
    }
  }, [activeDownloadParams, onDownloadSurveyResponsesAsync, cancelDownload]);

  const handleDownload = useCallback(
    async (surveyDownloadParams: SurveyDownloadParams) => {
      const result = await onDownloadSurveyResponses(surveyDownloadParams);

      if (result === 'tooLarge') {
        setActiveDownloadParams(surveyDownloadParams);
        setIsFileTooLarge(true);
        return;
      }

      hotjar.event('downloaded_responses');
    },
    [onDownloadSurveyResponses],
  );

  return useMemo(
    () => ({
      isFileTooLarge,
      continueDownload,
      handleDownload,
      cancelDownload,
    }),
    [isFileTooLarge, continueDownload, handleDownload, cancelDownload],
  );
};
